<template>
  <a-collapse
    v-model:activeKey="activeKey"
    :bordered="false"
    class="collapses"
    style="background: rgb(255, 255, 255)"
  >
    <template #expandIcon="{ isActive }">
      <caret-right-outlined :rotate="isActive ? 90 : 0" />
    </template>
    <a-collapse-panel
      key="personal"
      :style="panelStyle"
    >
      <template #header>
        <span style="font-weight: 700">{{ $t('个人视图') }}</span>
      </template>
      <template #extra>
        <a-tooltip>
          <template #title>{{ $t('个人视图管理') }}</template>
          <setting-outlined
            style="margin-right: 10px"
            @click.stop="handleManageView('personal')"
          />
        </a-tooltip>
        <a-tooltip>
          <template #title>{{ $t('新建个人视图') }}</template>
          <plus-outlined @click.stop="addView('personal')" />
        </a-tooltip>
      </template>
      <s-table
        v-if="personData?.length > 0"
        :selectedRowKeys="selectedRowKeys"
        :columns="viewTableColumns"
        :dataSource="personData"
        :pagination="false"
        size="small"
        :showHeader="false"
        row-key="id"
        :range-selection="false"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.dataIndex === 'name'">
            <ViewConfigCell
              :fetchViews="fetchViews"
              :record="record"
              :currentView="currentView"
              :setViewData="setViewData"
              :setCurrentView="setCurrentView"
            />
          </template>
        </template>
      </s-table>
    </a-collapse-panel>
    <a-collapse-panel
      key="public"
      :style="panelStyle"
    >
      <template #header>
        <span style="font-weight: 700">{{ $t('公共视图') }}</span>
      </template>
      <template #extra>
        <a-tooltip>
          <template #title>{{ $t('公共视图管理') }}</template>
          <setting-outlined
            style="margin-right: 10px"
            @click.stop="handleManageView('public')"
          />
        </a-tooltip>
        <a-tooltip>
          <template #title>{{ $t('新建公共视图') }}</template>
          <plus-outlined @click.stop="addView('public')" />
        </a-tooltip>
      </template>
      <s-table
        v-if="publicData?.length > 0"
        :selectedRowKeys="selectedRowKeys"
        :columns="viewTableColumns"
        :dataSource="publicData"
        :pagination="false"
        size="small"
        :showHeader="false"
        row-key="id"
        :range-selection="false"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.dataIndex === 'name'">
            <ViewConfigCell
              :fetchViews="fetchViews"
              :record="record"
              :currentView="currentView"
              :setViewData="setViewData"
              :setCurrentView="setCurrentView"
            />
          </template>
        </template>
      </s-table>
    </a-collapse-panel>
  </a-collapse>
</template>

<script setup lang="ts">
import { computed, ref, reactive, onMounted, PropType, watch } from 'vue';
import { SettingOutlined, PlusOutlined, CaretRightOutlined } from '@ant-design/icons-vue';
import { useI18n } from 'vue-i18n';
import { create_view_settings, change_view_settings_sort } from 'liyu-pc-base/api/view-model';
import { message } from 'ant-design-vue';
import modalBox from 'liyu-pc-base/components/form-modal/modal-tools';
import { useUserStore } from 'liyu-pc-base/init-plugin';
import ViewConfigCell from './viewConfigCell.vue';

const props = defineProps({
  view_key: {
    type: String,
    default: '',
  },
  view_flag_name: {
    type: String,
    default: '',
  },
  tableProState: {
    type: Object,
    default: () => null,
  },
  eventBus: {
    type: Object as PropType<any>,
    default: () => null,
  },
  hashPrefix: {
    type: String,
    default: '',
  },
});

const activeKey = ref(['personal', 'public']);
const panelStyle =
  'background: #ffffff;border-radius: 4px;margin-bottom: 2px;border: 0;overflow: hidden;';
const { t } = useI18n();
const userStore = useUserStore();
const current_org = userStore.currentOrg;
const userid = userStore.userid;
// ★ v24（决策 24）：不做按钮权限校验 —— 不 CHECK_AUTH(VIEW_SYSTEM_EDIT)，
// 系统视图的创建/修改/删除/设默认一律放行。
const setViewData = (data: any) => props.tableProState?.setViewData(data);
const getViewData = () => props.tableProState?.getViewData();
const viewDataList = computed(() => {
  return props.tableProState?.viewDataList || [];
});
const fetchViews = () => props.tableProState?.fetchViews();
const currentView = computed({
  get: () => props.tableProState?.currentView,
  set: val => {
    props.tableProState.currentView = val;
  },
});
const setCurrentView = (view: any) => {
  props.tableProState.currentView = view;
};

const viewTableColumns = [{ dataIndex: 'name', key: 'name' }];
const selectedRowKeys = computed(() => {
  return [currentView.value?.id];
});

const personData = computed(() => {
  const list = (viewDataList.value || [])
    .filter((item: any) => !!item.user_id)
    .sort((a, b) => (a.sort == b.sort ? a.id - b.id : a.sort - b.sort));
  return list;
});

const publicData = computed(() => {
  const list = (viewDataList.value || [])
    .filter((item: any) => !item.user_id)
    .sort((a, b) => (a.sort == b.sort ? a.id - b.id : a.sort - b.sort));
  return list;
});

const addView = (view_type: string) => {
  const tempModal = reactive({
    open: true,
    loading: false,
    title: t('新建视图'),
    form: {
      settings: { labelAlign: 'right', layout: 'vertical', col: [] },
      fields: [
        {
          type: 'input',
          name: 'name',
          label: t('视图名称'),
          disabled: false,
          allowClear: true,
          inputType: 'text',
          defaultValue: '',
          placeholder: t('请输入视图名称'),
        },
      ],
      rules: reactive({
        name: [{ required: true, message: t('请输入视图名称') }],
      }),
      model: reactive({ name: '', is_system: [false] }),
    },
    options: {
      view_type: [
        { name: t('个人视图（仅自己可见）'), value: 'personal' },
        { name: t('公共视图（项目成员均可见）'), value: 'public' },
      ],
      is_system: [{ name: t('设为系统视图'), value: true }],
    },
    ok: async (data: any) => {
      const type = view_type || data.view_type || 'personal';
      const res = await create_view_settings({
        org_id: current_org.id,
        user_id: type === 'personal' ? userid : null,
        name: data.name,
        view_project__flag: process.env.VITE_APP_VIEW_PROJECT_FLAG,
        view_menu__flag: props.view_key,
        view_menu__name: props.view_flag_name,
        is_system: data.is_system && data.is_system[0],
        data: getViewData(),
      });
      await fetchViews();
      currentView.value = res;
      message.success(t('创建成功'));
    },
  });
  if (!view_type) {
    tempModal.form.fields.push({
      type: 'radio',
      name: 'view_type',
      label: t('视图可见范围'),
      disabled: false,
      allowClear: true,
      labelKey: 'name',
      valueKey: 'value',
      defaultValue: '',
      placeholder: '',
    } as any);
  }

  // ★ v24：系统视图选项不再做权限门控，恒展示
  tempModal.form.fields.push({
    type: 'checkbox',
    name: 'is_system',
    label: '',
    disabled: false,
    allowClear: true,
    labelKey: 'name',
    valueKey: 'value',
    defaultValue: [false],
  } as any);
  modalBox(tempModal);
};

const handleManageView = (view_type: string) => {
  const tempModal = reactive({
    open: true,
    component: 'ViewEditModal',
    compObj: {
      title: view_type == 'personal' ? t('个人视图管理') : t('公共视图管理'),
      data: view_type == 'personal' ? personData.value : publicData.value,
    },
    ok: (ids: any) => {
      if (ids && ids.length > 0) {
        change_view_settings_sort({
          org_id: current_org.id,
          view_settings_ids: ids,
        }).then(() => {
          message.success(t('保存成功'));
          fetchViews();
        });
      } else {
        message.success(t('保存成功'));
        fetchViews();
      }
    },
    cancel: (isEdit: any) => {
      if (isEdit) {
        fetchViews();
      }
    },
  });
  modalBox(tempModal);
};
watch(
  () => props.tableProState?.currentView,
  newVal => {
    if (newVal) {
      window.location.hash = `${props.hashPrefix}${newVal.uid}`;
    }
  },
);
onMounted(() => {
  fetchViews();
});
</script>

<style lang="less">
.collapses .ant-collapse-item .ant-collapse-content .ant-collapse-content-box {
  padding: 0;
}
</style>
